import React, { createContext, useCallback, useContext, useState } from "react";
import { 
  getWarehousesPaginated,
  getWarehouses, 
  getWarehouseById, 
  createWarehouse, 
  updateWarehouse, 
  deleteWarehouse, 
  WarehouseApiResponse
} from "../../services/warehouseService";
import { getErrorMessage } from "../../locales/translationUtils";
import LoadingOverlay from "../../components/Loading";
import Warehouse from "../../types/warehouse";
import Paginacao from "../../types/paginacao";
import { ApiResponse } from "../../services/api";

interface WarehouseContextProps {
  apiResponse: ApiResponse<Warehouse> | null;
  warehouses: Warehouse[] | null;
  warehouseById: Warehouse | null;
  loading: boolean;
  error: string | null;
  fetchWarehouse: (paginacao: Paginacao | null) => void;
  fetchWarehouseById: (id: number) => Promise<Warehouse | null>;
  postWarehouse: (warehouse: Warehouse) => Promise<WarehouseApiResponse | null>;
  putWarehouse: (id: number, warehouse: Warehouse) => Promise<WarehouseApiResponse | null>;
  excluiWarehouse: (id: number) => Promise<boolean>;
}

interface WarehouseProviderProps {
  children: React.ReactNode;
}

const WarehouseContext = createContext<WarehouseContextProps | null>(null);

const WarehouseProvider: React.FC<WarehouseProviderProps> = ({ children }) => {
  const [apiResponse, setApiResponse] = useState<ApiResponse<Warehouse> | null>(null);
  const [warehouses, setWarehouse] = useState<Warehouse[] | null>(null);
  const [warehouseById, setWarehouseById] = useState<Warehouse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWarehouse = useCallback(async (paginacao: Paginacao | null) => {
    setError(null);
    try {
      setLoading(true);
      const response = (paginacao) ? await getWarehousesPaginated(paginacao) : await getWarehouses();
      setApiResponse(response);
      setWarehouse(response.data.values || []);
    } catch (err) {
      setWarehouse([]);
      setError(getErrorMessage("fetchWarehouseError"));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWarehouseById = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      const response = await getWarehouseById(id);
      
      setWarehouseById(response.data);
      return response.data;
    } catch (err) {
      setError(getErrorMessage("fetchWarehouseByIdError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const postWarehouse = useCallback(async (warehouse: Warehouse) => {
    setError(null);
    try {
      setLoading(true);
      const response = await createWarehouse(warehouse);
      return response;
    } catch (err) {
      setError(getErrorMessage("createWarehouseError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const putWarehouse = useCallback(async (id: number, warehouse: Warehouse) => {
    setError(null);
    try {
      setLoading(true);
      const response = await updateWarehouse(id, warehouse);
      return response;
    } catch (err) {
      setError(getErrorMessage("updateWarehouseError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const excluiWarehouse = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      await deleteWarehouse(id);
      return true;
    } catch (err) {
      setError(getErrorMessage("deleteWarehouseError"));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <WarehouseContext.Provider 
      value={{ 
        apiResponse,
        warehouses, 
        warehouseById, 
        loading, 
        error, 
        fetchWarehouse, 
        fetchWarehouseById, 
        postWarehouse, 
        putWarehouse, 
        excluiWarehouse 
      }}
    >
      <LoadingOverlay isLoading={loading} />
      {children}
    </WarehouseContext.Provider>
  );
};

export default WarehouseProvider;

export const useWarehouse = (): WarehouseContextProps => {
  const context = useContext(WarehouseContext);
  if (!context) {
    throw new Error(
      getErrorMessage("wrongUseProviderError", {
        auth: useWarehouse?.name,
        providerName: WarehouseProvider?.name,
      })
    );
  }
  return context;
};
